<script setup lang="ts">
/**
 * Phone field aligned with the sales journey: a country dial-code select
 * (flag + code, full name in the tooltip) plus a number input formatted live to
 * the selected country's national convention (libphonenumber-js, via
 * `@/utils/phone`). Two v-models: the national display string and the country.
 * The parent derives the canonical E.164 value with `toE164` on submit.
 */
import { computed, nextTick, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  COUNTRY_CODES_PHONE,
  COUNTRY_DIAL_CODES,
  DEFAULT_COUNTRY,
  countryLabel,
  flagEmoji,
  type PhoneCountryCode,
} from '@/data/countries'
import { formatPhoneAsYouType, isPhoneTooLong } from '@/utils/phone'
import type { Locale } from '@/i18n'

const props = withDefaults(
  defineProps<{
    /** National display string (e.g. "06 12 34 56 78"). */
    modelValue: string
    country: PhoneCountryCode
    invalid?: boolean
    inputId?: string
  }>(),
  { invalid: false, inputId: undefined },
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'update:country': [value: PhoneCountryCode]
}>()

const { t, locale } = useI18n()

const phoneCountry = computed<PhoneCountryCode>({
  get: () => props.country,
  set: (v) => emit('update:country', v),
})

const phoneDialCode = computed(() => COUNTRY_DIAL_CODES[props.country] ?? '')
const phoneCountryTitle = computed(
  () => `${countryLabel(props.country, locale.value as Locale)} (${phoneDialCode.value})`,
)

/** Options : drapeau + nom localisé + indicatif, triées par label, France en tête. */
const phoneCountryOptions = computed(() => {
  const collator = new Intl.Collator(locale.value, { sensitivity: 'base' })
  const labelled = COUNTRY_CODES_PHONE.map((code) => ({
    code,
    name: countryLabel(code, locale.value as Locale),
    flag: flagEmoji(code),
    dial: COUNTRY_DIAL_CODES[code],
  }))
  const fr = labelled.find((c) => c.code === DEFAULT_COUNTRY)
  const rest = labelled
    .filter((c) => c.code !== DEFAULT_COUNTRY)
    .sort((a, b) => collator.compare(a.name, b.name))
  return fr ? [fr, ...rest] : rest
})

function countDigits(text: string): number {
  return (text.match(/\d/g) ?? []).length
}
function caretAfterDigit(formatted: string, digitCount: number): number {
  if (digitCount <= 0) return 0
  let seen = 0
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i]) && ++seen === digitCount) return i + 1
  }
  return formatted.length
}

/** Reformate à la frappe et préserve la position du curseur (le binding `:value`
 *  le renverrait sinon en fin de champ). Refuse la saisie au-delà de la longueur
 *  max valide du pays. */
function onPhoneInput(event: Event) {
  const target = event.target as HTMLInputElement
  if (isPhoneTooLong(target.value, props.country)) {
    target.value = props.modelValue
    return
  }
  const caret = target.selectionStart ?? target.value.length
  const digitsBeforeCaret = countDigits(target.value.slice(0, caret))
  const formatted = formatPhoneAsYouType(target.value, props.country)
  emit('update:modelValue', formatted)
  nextTick(() => {
    const pos = caretAfterDigit(formatted, digitsBeforeCaret)
    target.setSelectionRange(pos, pos)
  })
}

// Changer de pays reformate le numéro déjà saisi à la convention du nouveau pays.
watch(
  () => props.country,
  () => {
    if (props.modelValue) emit('update:modelValue', formatPhoneAsYouType(props.modelValue, props.country))
  },
)
</script>

<template>
  <div class="phone-row" :class="{ 'phone-row--invalid': invalid }">
    <div class="jf-select phone-dial">
      <select
        v-model="phoneCountry"
        class="jf-select-el phone-dial-el"
        :aria-label="t('auth.account.fields.phone.dialLabel')"
        :title="phoneCountryTitle"
      >
        <option
          v-for="opt in phoneCountryOptions"
          :key="opt.code"
          :value="opt.code"
          :title="`${opt.name} (${opt.dial})`"
        >{{ opt.flag }} {{ opt.dial }}</option>
      </select>
      <svg class="jf-select-chevron" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
        <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>
    <input
      :id="inputId"
      :value="modelValue"
      type="tel"
      class="jf-input phone-input"
      :placeholder="t('auth.account.fields.phone.placeholder')"
      autocomplete="tel-national"
      inputmode="tel"
      @input="onPhoneInput"
    />
  </div>
</template>

<style scoped>
.jf-select { position: relative; display: flex; align-items: center; }
.jf-select-el {
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  padding: 0.7rem 2.5rem 0.7rem 0.9rem;
  background: var(--color-white);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  font: inherit;
  font-size: var(--text-base);
  color: var(--color-chocolate);
  cursor: pointer;
  transition: border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out);
}
.jf-select-el:focus { outline: none; border-color: var(--primary); box-shadow: 0 0 0 3px var(--focus-ring); }
.jf-select-chevron { position: absolute; right: 0.9rem; color: var(--text-muted); pointer-events: none; }

.jf-input {
  width: 100%;
  padding: 0.7rem 0.9rem;
  background: var(--color-white);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  font: inherit;
  font-size: var(--text-base);
  color: var(--color-chocolate);
  transition: border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out);
}
.jf-input::placeholder { color: var(--text-muted); }
.jf-input:focus { outline: none; border-color: var(--primary); box-shadow: 0 0 0 3px var(--focus-ring); }

.phone-row { display: flex; align-items: stretch; gap: 0.5rem; }
.phone-dial { position: relative; flex: 0 0 7.75rem; min-width: 0; }
.phone-dial-el { width: 100%; padding-right: 2.75rem; }
.phone-input { flex: 1; min-width: 0; }
.phone-row--invalid .phone-input,
.phone-row--invalid .phone-dial-el { border-color: var(--danger); }
</style>
